import {
  BarChart3,
  BookOpen,
  BriefcaseBusiness,
  Clapperboard,
  FileSearch,
  Library,
  Megaphone,
  MessageSquareQuote,
  Scissors,
  Search,
  Share2,
  Sparkles,
  Users,
} from "lucide-react";

export const workflow = [
  { title: "Extract Intel", description: "AI turns long-form video into structured chapters, summaries, and quotes.", icon: Sparkles },
  { title: "Find Moments", description: "Search across every video and jump directly to the signal.", icon: Search },
  { title: "Create Clips", description: "Turn the exact moment you need into a focused, shareable clip.", icon: Scissors },
  { title: "Build Your Library", description: "Keep the intelligence your team needs organized and searchable.", icon: Library },
  { title: "Publish & Share", description: "Move proof, insight, and content into the channels where GTM works.", icon: Share2 },
];

export const useCases = [
  {
    slug: "sales-enablement",
    label: "Sales Enablement",
    title: "Give every rep your best moments.",
    description: "Turn demos, customer interviews, webinars, and presentations into searchable clips and knowledge your sales team can find and share.",
    icon: BriefcaseBusiness,
  },
  {
    slug: "business-development",
    label: "Business Development",
    title: "Never lose the signal inside another video.",
    description: "Capture insights from conferences, interviews, market events, and industry content—and build an intelligence library that compounds over time.",
    icon: BarChart3,
  },
  {
    slug: "product-marketing",
    label: "Product Marketing",
    title: "Turn video into market intelligence.",
    description: "Extract customer insight, competitive intelligence, messaging, quotes, and proof points without rewatching hours of video.",
    icon: Users,
  },
  {
    slug: "content-marketing",
    label: "Content Marketing",
    title: "Long-form in. GTM content out.",
    description: "Find the moments worth sharing, create clips, build briefs, and get more value from every piece of video you produce.",
    icon: Megaphone,
  },
];

export const capabilityCards = [
  { title: "Search the substance", description: "Find topics, names, claims, and quotes across an expanding video library.", icon: FileSearch },
  { title: "Keep the context", description: "Move from a quote to its chapter, source video, and exact timecode.", icon: BookOpen },
  { title: "Make proof portable", description: "Turn a strong moment into a clip your team can send, publish, or embed.", icon: Clapperboard },
  { title: "Brief the team", description: "Give GTM teams the insight without asking them to rewatch the source.", icon: MessageSquareQuote },
];

export const useCasePages = {
  "sales-enablement": {
    eyebrow: "VIDEOBRIEF FOR SALES ENABLEMENT",
    title: "Your best sales content already exists.",
    accent: "Make it findable.",
    intro: "Your reps should not have to remember which webinar, demo, customer interview, or presentation contained the perfect answer. VideoBrief turns your video library into searchable sales intelligence—and lets reps ship the right clip when they find it.",
    question: "Do we have a customer talking about reducing implementation time?",
    result: "Customer proof found",
    resultDetail: "0:47 clip · ready to share",
    image: "/marketing/videobrief/chapters.jpg",
    imageAlt: "VideoBrief chapters and quotes organized by exact timecode",
    outcomes: [
      ["Faster answers", "Find the exact objection response, product explanation, or customer proof without asking around."],
      ["Reusable proof", "Turn customer language into precise clips that stay connected to their source."],
      ["A smarter library", "Every webinar, demo, and presentation makes the next rep better prepared."],
    ],
    pillars: ["Customer proof", "Objection handling", "Product demos", "Shareable clips"],
    icon: BriefcaseBusiness,
  },
  "business-development": {
    eyebrow: "VIDEOBRIEF FOR BUSINESS DEVELOPMENT",
    title: "Watch the market.",
    accent: "Without watching everything.",
    intro: "Turn conferences, interviews, webinars, and industry video into a continuously growing intelligence library. Surface the companies, themes, claims, and moments that matter before the next opportunity moves.",
    question: "What changed across this three-hour industry conference?",
    result: "14 companies · 8 strategic insights",
    resultDetail: "17 notable quotes · 6 competitor mentions · 9 clips saved",
    image: "/marketing/videobrief/magic-quotes.jpg",
    imageAlt: "VideoBrief extracting targeted market insights from a video chapter",
    outcomes: [
      ["See the landscape", "Capture company mentions, market shifts, and strategic claims across long-form video."],
      ["Save the evidence", "Keep the quote and clip behind each insight, not just a secondhand summary."],
      ["Compound your intel", "Build a searchable record of what partners, competitors, and markets are saying."],
    ],
    pillars: ["Market signals", "Partner intelligence", "Competitor mentions", "Conference briefs"],
    icon: BarChart3,
  },
  "product-marketing": {
    eyebrow: "VIDEOBRIEF FOR PRODUCT MARKETING",
    title: "Your market is telling you things.",
    accent: "Capture what matters.",
    intro: "Customers and competitors reveal positioning, pain points, language, and proof across webinars, launches, interviews, and product content. VideoBrief makes those signals searchable and usable.",
    question: "How are customers describing the problem in their own words?",
    result: "Messaging pattern identified",
    resultDetail: "12 supporting quotes · 4 proof clips",
    image: "/marketing/videobrief/ai-settings.jpg",
    imageAlt: "VideoBrief intelligence extraction settings for targeted product marketing research",
    outcomes: [
      ["Hear customer language", "Collect the exact phrases customers use for pains, outcomes, and alternatives."],
      ["Track competitors", "Find positioning shifts, product claims, and launch narratives in video content."],
      ["Arm every launch", "Move sourced insight and proof into briefs, messaging, enablement, and campaigns."],
    ],
    pillars: ["Customer intelligence", "Competitive intel", "Messaging", "Proof points"],
    icon: Users,
  },
  "content-marketing": {
    eyebrow: "VIDEOBRIEF FOR CONTENT MARKETING",
    title: "One video.",
    accent: "A lot more content.",
    intro: "Automatically find the strongest moments inside long-form video and turn them into clips, briefs, quotes, and publishable content—without starting every asset from a blank page.",
    question: "Which moments from this webinar deserve a second life?",
    result: "9 high-signal moments found",
    resultDetail: "6 clips · 14 quotes · one complete brief",
    image: "/marketing/videobrief/clip-workflow.jpg",
    imageAlt: "VideoBrief showing multiple quotable moments selected for content clips",
    outcomes: [
      ["Find the hook", "Surface the strongest claims, stories, and quotable moments from every recording."],
      ["Ship more formats", "Create focused clips, written briefs, quote cards, and source material for campaigns."],
      ["Extend the half-life", "Keep useful long-form content working long after the live moment ends."],
    ],
    pillars: ["Clips", "Briefs", "Quotes", "Publishable content"],
    icon: Megaphone,
  },
};
